import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { withStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";

// Components
import MDCLabel from "../../core/MDCLabel";
import MDCButton from "../../core/MDCButton";
import MDCLoader from "../../core/MDCLoader";
import MDCStepper from "../../core/MDCStepper";
import QESSupportDataBox from "../QESSupportDataBox";
import QESProviderBanner from "../../shared/QESProviderBanner";

// Constants
import { VollmachtFormConstants } from "../../../constants/ManualRequestFormConstants";
import { COMPONENT_IDS } from "../../../constants/RolePermissions";

// Utils
import { hasRolePermissions } from "../../../helpers/utils";

// Icons
import RefreshIcon from "../../iconComponents/RefreshIcon";

// Assets
import verimiImage from "../../../assets/images/verimi-image.PNG";
import downloadPDFLogo from "../../../assets/images/Icon_Download_Black.svg";

const styles = () => ({
  qesPreviewSection: {
    marginTop: "-1.5rem",
  },
  marginBottomStepper: {
    marginBottom: "2rem",
  },
  vollmachtStep: {
    display: "flex",
    flexDirection: "column !important",
    marginBottom: "3rem !important",
  },
  vollmachtStep3rd: {
    textAlign: "left",
  },
  width75: {
    width: "75%",
  },
  QRLoader: {
    marginTop: "1.5rem",
    height: "87px",
    width: "18em",
    display: "flex",
    justifyContent: "center",
  },
  vollmachtImage: {
    backgroundRepeat: "no-repeat",
    backgroundSize: "100%",
  },
  verimiImage: {
    backgroundImage: `url(${verimiImage})`,
    height: "300px",
    backgroundSize: "90%",
    backgroundPosition: "center",
  },
  vollmachtDownloadPDF: {
    backgroundImage: `url(${downloadPDFLogo})`,
    height: "22px",
    width: "22px",
  },
  downloadInput: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "80%",
    padding: "0.3rem 0.8rem",
    borderRadius: "5px",
    border: `1px solid #BDBDBD`,
    color: "black",
    marginBottom: "1rem",
    cursor: "pointer",
    "& >label": {
      display: "block",
      flex: 1,
      minWidth: 0,
      marginBottom: 0,
      marginRight: "0.5rem",
      cursor: "pointer",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& >div:last-child": {
      flexShrink: 0,
    },
  },
  circleLoader: {
    marginBottom: "5.5rem",
    border: "3px solid rgba(0, 0, 0, 0.2)",
    borderColor: "#BDBDBD",
    position: "relative",
    display: "inline-block",
    verticalAlign: "top",
    borderRadius: "50%",
    width: "11rem",
    height: "11rem",
  },
  checkMarkDraw: {
    opacity: 1,
    height: "5.5rem",
    width: "2.75rem",
    transformOrigin: "left top",
    borderRight: "3px solid #BDBDBD",
    borderTop: "3px solid #BDBDBD",
    content: "",
    left: "2.75rem",
    top: "5.5rem",
    position: "absolute",
    animationDuration: "3000ms",
    animationTimingFunction: "ease",
    animationName: "checkmark",
    transform: "scaleX(-1) rotate(135deg)",
  },
  "@keyframes checkmark": {
    "0%": {
      height: 0,
      width: 0,
      opacity: 1,
    },
    "20%": {
      height: 0,
      width: "2.75rem",
      opacity: 1,
    },
    "40%": {
      height: "5.5rem",
      width: "2.75rem",
      opacity: 1,
    },
    "100%": {
      height: "5.5rem",
      width: "2.75rem",
      opacity: 1,
    },
  },
  contentLeftAlign: {
    textAlign: "left",
  },
});

const isAllowedBasketInfoRead = hasRolePermissions(
  COMPONENT_IDS.VERIMI_BASKET_INFO,
  "read"
);

const VerimiQESFlow = ({
  classes,
  blinkingValue,
  QRCodeURL,
  loadingQRCode,
  showCheckmark,
  qesSupportData,
  vollmachtSigningStatus,
  formValues,
  halterFormValues,
  isDetailDialog,
  isVollmachtCompleted,
  isAdminAndSupportUser,
  selectedQESVendor,
  onGenerateQRCode,
  onHandleFile,
  onCancelRequest,
}) => {
  const PREVIEW = "PREVIEW";
  const DOWNLOAD = "DOWNLOAD";

  const vendorBanner = selectedQESVendor?.vendor ? (
    <QESProviderBanner
      provider={selectedQESVendor.vendor}
      onCancel={onCancelRequest}
      isComplete={isVollmachtCompleted}
      disabled={isVollmachtCompleted}
    />
  ) : null;

  const qesStepOneLocalIdentView = (
    <>
      <Grid marginBottom="2.5rem">
        <MDCLabel label="Scan" variant="h6" />
        <p className={classes.width75}>
          {VollmachtFormConstants.STEP_ONE_CONTENT}
        </p>
      </Grid>
      <div>
        <MDCButton
          variant="outlined"
          label={"Neuen QR Code generieren"}
          onClick={onGenerateQRCode}
          disabled={isAdminAndSupportUser || isDetailDialog}
          endIcon={<RefreshIcon />}
        />
      </div>
      <div>
        {loadingQRCode ? (
          <div className={classes.QRLoader}>
            <MDCLoader />
          </div>
        ) : (
          <>{QRCodeURL && <img alt="Vollmacht QR Code" src={QRCodeURL} />}</>
        )}
      </div>
    </>
  );

  const qesStepTwoLocalIdentView = (
    <>
      <Grid textAlign="center" marginBottom="1.5rem">
        <MDCLabel label="Vollmachterstellung via Verimi" variant="h6" />
        <p>{VollmachtFormConstants.STEP_TWO_CONTENT}</p>
      </Grid>

      <div className={`${classes.vollmachtImage} ${classes.verimiImage}`} />
    </>
  );

  const qesStepThreeLocalIdentView = (
    <>
      <p className={classes.contentLeftAlign}>
        {VollmachtFormConstants.STEP_THREE_LOCAL_CONTENT}
      </p>
    </>
  );

  return (
    <div className={classes.qesPreviewSection}>
      {vendorBanner}
      <div className={classes.marginBottomStepper}>
        <MDCStepper step={blinkingValue} />
      </div>
      <Grid container spacing={3}>
        <Grid
          item
          xs={4}
          className={classes.vollmachtStep}
          id="step-1-container"
        >
          {qesStepOneLocalIdentView}
        </Grid>

        <Grid item xs={4} className={classes.vollmachtStep}>
          {qesStepTwoLocalIdentView}
        </Grid>

        <Grid
          item
          xs={4}
          className={`${classes.vollmachtStep} ${classes.vollmachtStep3rd}`}
          id="step-3-container"
        >
          <Grid width="18rem" marginBottom="2.5rem">
            <MDCLabel label="Vollmacht empfangen" variant="h6" />
            {qesStepThreeLocalIdentView}
          </Grid>

          {!showCheckmark ? (
            blinkingValue > 2 && (
              <>
                <Grid marginTop="2.5rem" marginBottom="1rem">
                  Erstellt am{" "}
                  {moment(vollmachtSigningStatus?.signedDate).format(
                    "DD.MM.YYYY"
                  )}{" "}
                  um{" "}
                  {`${moment(vollmachtSigningStatus?.signedDate).format(
                    "HH:mm"
                  )} Uhr und betreut durch ${
                    vollmachtSigningStatus?.initiatedByUser
                  }`}
                </Grid>
                <div className={classes.downloadInput}>
                  <label
                    onDoubleClick={() =>
                      onHandleFile(
                        formValues?.vollmachtsprozess?.value,
                        PREVIEW
                      )
                    }
                  >
                    {formValues?.vollmachtsprozess?.value === "Einzelvollmacht"
                      ? "Einzelvollmacht"
                      : "Dauervollmacht"}
                  </label>
                  <div
                    onClick={() =>
                      onHandleFile(
                        formValues?.vollmachtsprozess?.value,
                        DOWNLOAD
                      )
                    }
                    className={`${classes.vollmachtImage} ${classes.vollmachtDownloadPDF}`}
                  />
                </div>
                {!(
                  halterFormValues.Vorhanden.value ||
                  halterFormValues.steuerbefreiung.value
                ) && (
                  <div className={classes.downloadInput}>
                    <label
                      onDoubleClick={() => onHandleFile("SEPA-Mandat", PREVIEW)}
                    >
                      SEPA-Mandat
                    </label>
                    <div
                      onClick={() => {
                        onHandleFile("SEPA-Mandat", DOWNLOAD);
                      }}
                      className={`${classes.vollmachtImage} ${classes.vollmachtDownloadPDF}`}
                    />
                  </div>
                )}

                {halterFormValues?.addressDocument?.value ? (
                  <div className={classes.downloadInput}>
                    <label
                      onDoubleClick={() =>
                        onHandleFile("Adressnachweis", PREVIEW)
                      }
                    >
                      Adressnachweis
                    </label>
                    <div
                      onClick={() => onHandleFile("Adressnachweis", DOWNLOAD)}
                      className={`${classes.vollmachtImage} ${classes.vollmachtDownloadPDF}`}
                    />
                  </div>
                ) : null}
              </>
            )
          ) : (
            <div className={classes.circleLoader}>
              <div className={classes.checkMarkDraw} />
            </div>
          )}

          {isAllowedBasketInfoRead &&
            Object.keys(qesSupportData).length > 0 &&
            qesSupportData?.qesType && (
              <QESSupportDataBox qesSupportData={qesSupportData} />
            )}
        </Grid>
      </Grid>
    </div>
  );
};

VerimiQESFlow.propTypes = {
  classes: PropTypes.object.isRequired,
  blinkingValue: PropTypes.number.isRequired,
  QRCodeURL: PropTypes.string,
  loadingQRCode: PropTypes.bool.isRequired,
  showCheckmark: PropTypes.bool.isRequired,
  qesSupportData: PropTypes.object.isRequired,
  vollmachtSigningStatus: PropTypes.object,
  formValues: PropTypes.object.isRequired,
  halterFormValues: PropTypes.object.isRequired,
  isDetailDialog: PropTypes.bool,
  isVollmachtCompleted: PropTypes.bool,
  isAdminAndSupportUser: PropTypes.bool.isRequired,
  selectedQESVendor: PropTypes.object,
  onGenerateQRCode: PropTypes.func.isRequired,
  onHandleFile: PropTypes.func.isRequired,
  onCancelRequest: PropTypes.func.isRequired,
};

export default withStyles(styles)(VerimiQESFlow);
